// Langit & cuaca per-frame (dipisah dari engine.js, perilaku identik): gradasi langit, matahari/bulan,
// bintang, awan tinggi, awan di bawah pulau melayang, rintik hujan. Semua membaca atmosfer ter-lerp (c.atmos).
import { hashN } from "./sky";
import { cssCol } from "./color";

export function createSkyRender(c) {
  // c = { gridToScreen, cam, gridSize, get atmos }
  const GRID_SIZE = c.gridSize, cam = c.cam, gridToScreen = c.gridToScreen;
  let ctx = null, atmos = null;

  const STARS = Array.from({ length: 110 }, (_, i) => ({
    x: hashN(i * 3.17), y: hashN(i * 7.71) * 0.72,
    r: 0.5 + hashN(i * 13.3) * 1.2, tw: 0.6 + hashN(i * 29.9) * 2.2
  }));
  const SKY_CLOUDS = Array.from({ length: 4 }, (_, i) => ({
    u: hashN(i * 5.3), y: 0.06 + hashN(i * 9.1) * 0.3,
    s: 0.7 + hashN(i * 4.7) * 0.9, v: 0.004 + hashN(i * 8.9) * 0.006
  }));
  const UNDER_CLOUDS = Array.from({ length: 7 }, (_, i) => ({
    u: i / 7 + hashN(i * 6.1) * 0.08, dy: 24 + hashN(i * 11.7) * 85,
    s: 0.6 + hashN(i * 3.9) * 1.1, v: 0.0035 + hashN(i * 7.3) * 0.005, ph: hashN(i * 17.7) * 9
  }));
  const DROPS = Array.from({ length: 170 }, (_, i) => ({
    x: hashN(i * 3.7), y: hashN(i * 9.3), l: 9 + hashN(i * 5.1) * 12, v: 9 + hashN(i * 2.9) * 7
  }));

  // Gumpalan awan lembut (3 elips tumpang-tindih)
  function puff(x, y, r, a) {
    if (a <= 0.01) return;
    ctx.fillStyle = `rgba(255,255,255,${Math.min(1, a).toFixed(3)})`;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.42, 0, 0, Math.PI * 2);
    ctx.ellipse(x - r * 0.55, y + r * 0.1, r * 0.6, r * 0.32, 0, 0, Math.PI * 2);
    ctx.ellipse(x + r * 0.5, y + r * 0.12, r * 0.65, r * 0.3, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // ---- Langit: gradasi + matahari/bulan + bintang + awan tinggi ----
  function drawSky(W, H, t, hour) {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, cssCol(atmos.top));
    g.addColorStop(1, cssCol(atmos.bot));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    const day = 1 - atmos.dark;
    // Matahari — busur melintang mengikuti jam (06 timur → 18 barat)
    const sunT = (hour - 6) / 12;
    if (sunT > -0.05 && sunT < 1.05 && day > 0.05) {
      const cl = Math.max(0, Math.min(1, sunT));
      const sx = W * (0.12 + 0.76 * cl);
      const sy = H * (0.62 - 0.5 * Math.sin(Math.PI * cl));
      const r = 26 + 30 * day;
      const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 2.6);
      glow.addColorStop(0, `rgba(255,244,200,${(0.9 * day).toFixed(3)})`);
      glow.addColorStop(0.35, `rgba(255,236,170,${(0.35 * day).toFixed(3)})`);
      glow.addColorStop(1, "rgba(255,236,170,0)");
      ctx.fillStyle = glow;
      ctx.beginPath(); ctx.arc(sx, sy, r * 2.6, 0, Math.PI * 2); ctx.fill();
    }
    // Bulan saat gelap — busur malam (18 timur → 06 barat)
    if (atmos.dark > 0.35) {
      const moonT = Math.max(0, Math.min(1, ((hour + 24 - 18) % 24) / 12));
      const mx = W * (0.14 + 0.72 * moonT);
      const my = H * (0.55 - 0.4 * Math.sin(Math.PI * moonT));
      const a = (atmos.dark - 0.35) / 0.65;
      const glow = ctx.createRadialGradient(mx, my, 0, mx, my, 44);
      glow.addColorStop(0, `rgba(238,242,255,${(0.9 * a).toFixed(3)})`);
      glow.addColorStop(0.4, `rgba(220,228,250,${(0.25 * a).toFixed(3)})`);
      glow.addColorStop(1, "rgba(220,228,250,0)");
      ctx.fillStyle = glow;
      ctx.beginPath(); ctx.arc(mx, my, 44, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(244,247,255,${(0.95 * a).toFixed(3)})`;
      ctx.beginPath(); ctx.arc(mx, my, 11, 0, Math.PI * 2); ctx.fill();
    }
    // Bintang berkelip
    if (atmos.dark > 0.2) {
      const a = (atmos.dark - 0.2) / 0.8;
      for (const s of STARS) {
        const tw = 0.5 + 0.5 * Math.sin(t * s.tw + s.x * 40);
        ctx.fillStyle = `rgba(255,255,255,${(a * tw * 0.9).toFixed(3)})`;
        ctx.fillRect(s.x * W, s.y * H, s.r, s.r);
      }
    }
    // Awan tinggi berarak di langit
    const cloudA = Math.max(0.25, day) * (1 - atmos.rain * 0.5);
    for (const c of SKY_CLOUDS) {
      const u = ((c.u + t * c.v) % 1.25) - 0.12;
      puff(u * W, c.y * H, 34 * c.s, cloudA * 0.8);
    }
  }

  // ---- Awan di bawah pulau melayang ----
  function drawUnderClouds(t) {
    const L = gridToScreen(0, GRID_SIZE), B = gridToScreen(GRID_SIZE, GRID_SIZE), R = gridToScreen(GRID_SIZE, 0);
    const zs = Math.max(0.4, Math.min(1.1, cam.zoom));
    for (const c of UNDER_CLOUDS) {
      const u = ((c.u + t * c.v) % 1.15) - 0.075;
      const x = L[0] + (R[0] - L[0]) * u;
      const y = B[1] + c.dy * zs + Math.sin(t * 0.5 + c.ph) * 7;
      puff(x, y, 40 * c.s * zs, 0.85);
    }
  }

  // ---- Rintik hujan ----
  function drawRain(W, H, t) {
    ctx.strokeStyle = `rgba(178,198,226,${(0.3 + 0.3 * atmos.rain).toFixed(3)})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const d of DROPS) {
      const y = (((d.y + t * d.v * 0.12) % 1.1) * H) - H * 0.05;
      const x = (((d.x - t * d.v * 0.018) % 1.1 + 1.1) % 1.1) * W - W * 0.05;
      ctx.moveTo(x, y);
      ctx.lineTo(x - d.l * 0.22, y + d.l);
    }
    ctx.stroke();
  }

  return {
    drawSky(g, W, H, t, hour) { ctx = g; atmos = c.atmos; drawSky(W, H, t, hour); },
    drawUnderClouds(g, t) { ctx = g; atmos = c.atmos; drawUnderClouds(t); },
    drawRain(g, W, H, t) { ctx = g; atmos = c.atmos; drawRain(W, H, t); }
  };
}
